'use client';

import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import Image from 'next/image';

interface FloatingIllustrationProps {
    children?: React.ReactNode;
    imageSrc?: string; // Add support for image source
    imageSize?: number; // Size for the image
    className?: string;
    speed?: number;
    direction?: 'up' | 'down';
    rotate?: boolean;
    scale?: boolean;
}

export default function FloatingIllustration({
    children,
    imageSrc,
    imageSize = 200,
    className = '',
    speed = 0.5,
    direction = 'up',
    rotate = true,
    scale = true,
}: FloatingIllustrationProps) {
    const elementRef = useRef<HTMLDivElement>(null);
    const reduceMotion = useReducedMotion();
    const { scrollYProgress } = useScroll({
        target: elementRef,
        offset: ['start end', 'end start'],
    });
    const yMovement = direction === 'up' ? -100 * speed : 100 * speed;
    const rotation = rotate ? (direction === 'up' ? -5 : 5) : 0;
    const y = useTransform(scrollYProgress, [0, 1], [0, reduceMotion ? 0 : yMovement]);
    const rotateValue = useTransform(scrollYProgress, [0, 1], [0, reduceMotion ? 0 : rotation]);
    const scaleValue = useTransform(scrollYProgress, [0, 1], [1, reduceMotion || !scale ? 1 : 1.1]);

    return (
        <motion.div
            ref={elementRef}
            className={`will-change-transform ${className}`}
            style={{ y, rotate: rotateValue, scale: scaleValue }}
        >
            {imageSrc ? (
                <div style={{ width: imageSize, height: imageSize, position: 'relative' }}>
                    <Image
                        src={imageSrc}
                        alt="Floating Illustration"
                        fill
                        className="object-contain"
                    />
                </div>
            ) : children}
        </motion.div>
    );
}

// Keep PlaceholderIllustration for fallback if needed
export function PlaceholderIllustration({
    type = 'circle',
    className = '',
    size = 200,
}: {
    type?: 'circle' | 'triangle' | 'star' | 'blob';
    className?: string;
    size?: number;
}) {
    // ... (keeping existing placeholder code just in case, or removing to save space)
    // For brevity, I'll allow children to be passed or imageSrc.
    return (
        <svg width={size} height={size} viewBox="0 0 200 200" fill="none" className={className}>
            <circle cx="100" cy="100" r="80" stroke="currentColor" strokeWidth="2" className="text-brand-text/20" />
        </svg>
    )
}
